import React, { useState, useEffect } from 'react';
import { reportsStorage } from '../../services/localStorage.service';
import './Reports.css';

// Icon Components
const DocumentIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4.5 2.25H13.5C13.9142 2.25 14.25 2.58579 14.25 3V15C14.25 15.4142 13.9142 15.75 13.5 15.75H4.5C4.08579 15.75 3.75 15.4142 3.75 15V3C3.75 2.58579 4.08579 2.25 4.5 2.25Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M6 5.25H12M6 9H12M6 12.75H9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ExcelIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3 3V15H15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M3 9H15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M3 3L9 9M15 3L9 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const Reports = () => {
  const [reportType, setReportType] = useState('Outstanding Dues Report');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reportData, setReportData] = useState([]);

  useEffect(() => {
    generateReport();
  }, [reportType, startDate, endDate]);

  const generateReport = () => {
    let data = [];
    
    switch (reportType) {
      case 'Outstanding Dues Report':
        data = reportsStorage.getOutstandingDues();
        if (startDate || endDate) {
          data = data.filter(item => {
            if (startDate && item.dueDate < startDate) return false;
            if (endDate && item.dueDate > endDate) return false;
            return true;
          });
        }
        break;
      case 'Sales Report':
        data = reportsStorage.getSalesReport(startDate, endDate);
        break;
      case 'Purchase Report':
        data = reportsStorage.getPurchaseReport(startDate, endDate);
        break;
      case 'Patient Report':
        data = reportsStorage.getPatientReport(startDate, endDate);
        break;
      default:
        data = reportsStorage.getOutstandingDues();
    }
    
    setReportData(data);
  };

  const handleGenerateReport = () => {
    generateReport();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2,
    }).format(amount || 0);
  };

  const getStatusBadge = (status) => {
    const statusLower = (status || '').toLowerCase();
    if (statusLower === 'overdue') return 'status-overdue';
    if (statusLower === 'pending') return 'status-pending';
    if (statusLower === 'partial') return 'status-partial';
    return 'status-confirmed';
  };

  const handleExportPDF = () => {
    alert('Exporting to PDF...');
  };

  const handleExportExcel = () => {
    alert('Exporting to Excel...');
  };

  return (
    <div className="reports">
      <div className="reports-header">
        <h1 className="page-title">Reports</h1>
      </div>

      <div className="reports-content">
        <div className="reports-card">
          <h2 className="card-title">Report Selection & Export</h2>
          <div className="report-form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="reportType">Report Type</label>
                <select
                  id="reportType"
                  value={reportType}
                  onChange={(e) => setReportType(e.target.value)}
                  className="form-input"
                >
                  <option>Outstanding Dues Report</option>
                  <option>Sales Report</option>
                  <option>Purchase Report</option>
                  <option>Patient Report</option>
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="startDate">Start Date</label>
                <input
                  type="date"
                  id="startDate"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label htmlFor="endDate">End Date (Optional)</label>
                <input
                  type="date"
                  id="endDate"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="form-input"
                />
              </div>
            </div>
            <div className="report-actions">
              <button onClick={handleGenerateReport} className="btn btn-primary" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <DocumentIcon />
                Generate Report
              </button>
              <button onClick={handleExportPDF} className="btn btn-secondary" style={{ backgroundColor: 'transparent', color: '#1e293b', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <DocumentIcon />
                Export PDF
              </button>
              <button onClick={handleExportExcel} className="btn btn-secondary" style={{ backgroundColor: 'transparent', color: '#1e293b', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <ExcelIcon />
                Export Excel
              </button>
            </div>
          </div>
        </div>

        <div className="reports-card">
          <h2 className="card-title">Report Preview: {reportType}</h2>
          <div className="report-table">
            {reportType === 'Outstanding Dues Report' ? (
              <table className="table">
                <thead>
                  <tr>
                    <th>Patient Name</th>
                    <th>Amount Due</th>
                    <th>Due Date</th>
                    <th>Reason</th>
                    <th>Responsible Person</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData.length === 0 ? (
                    <tr>
                      <td colSpan="6" style={{ textAlign: 'center', padding: '20px' }}>
                        No outstanding dues found
                      </td>
                    </tr>
                  ) : (
                    reportData.map((item, index) => (
                      <tr key={index}>
                        <td>{item.patient}</td>
                        <td className="amount-cell">{formatCurrency(item.amount)}</td>
                        <td>{item.dueDate}</td>
                        <td>{item.reason}</td>
                        <td>{item.responsible}</td>
                        <td>
                          <span className={`status-badge ${getStatusBadge(item.status)}`}>
                            {item.status}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            ) : reportType === 'Sales Report' ? (
              <table className="table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Patient Name</th>
                    <th>Amount</th>
                    <th>Payment Type</th>
                    <th>Collected</th>
                    <th>Remaining</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData.length === 0 ? (
                    <tr>
                      <td colSpan="6" style={{ textAlign: 'center', padding: '20px' }}>
                        No sales found
                      </td>
                    </tr>
                  ) : (
                    reportData.map((item, index) => (
                      <tr key={index}>
                        <td>{item.created_at ? new Date(item.created_at).toISOString().split('T')[0] : ''}</td>
                        <td>{item.patient_name || item.patientName}</td>
                        <td className="amount-cell">{formatCurrency(item.patient_sell_amount || item.patientSellAmount)}</td>
                        <td>{item.payment_type || item.paymentType}</td>
                        <td className="amount-cell">{formatCurrency(item.collected_amount || item.collectedAmount)}</td>
                        <td className="amount-cell">{formatCurrency(item.remaining_amount || item.remainingAmount)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            ) : reportType === 'Purchase Report' ? (
              <table className="table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Description</th>
                    <th>Amount</th>
                    <th>Category</th>
                    <th>Payment Type</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData.length === 0 ? (
                    <tr>
                      <td colSpan="5" style={{ textAlign: 'center', padding: '20px' }}>
                        No purchases found
                      </td>
                    </tr>
                  ) : (
                    reportData.map((item, index) => (
                      <tr key={index}>
                        <td>{item.date || (item.created_at ? new Date(item.created_at).toISOString().split('T')[0] : '')}</td>
                        <td>{item.description}</td>
                        <td className="amount-cell">{formatCurrency(item.amount)}</td>
                        <td>{item.category}</td>
                        <td>{item.payment_type || item.paymentType}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>Patient Name</th>
                    <th>Admit Date</th>
                    <th>Total Bill</th>
                    <th>Paid Amount</th>
                    <th>Remaining</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData.length === 0 ? (
                    <tr>
                      <td colSpan="5" style={{ textAlign: 'center', padding: '20px' }}>
                        No patients found
                      </td>
                    </tr>
                  ) : (
                    reportData.map((item, index) => {
                      const totalBill = parseFloat(item.total_bill || item.totalBill || 0);
                      const paidAmount = parseFloat(item.paid_amount || item.paidAmount || 0);
                      const remaining = totalBill - paidAmount;
                      return (
                        <tr key={index}>
                          <td>{item.patient_name || item.patientName}</td>
                          <td>{item.admit_date || item.admitDate || ''}</td>
                          <td className="amount-cell">{formatCurrency(totalBill)}</td>
                          <td className="amount-cell">{formatCurrency(paidAmount)}</td>
                          <td className="amount-cell">{formatCurrency(remaining)}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Reports;

